// Single task row used inside EveningShutdownModal — done checkbox + defer date picker.
import { addDaysIso } from "../lib/date";
import type { Task } from "../types";
import { BentoDateInput, PRIO } from "./bento-ui";

/** Per-task local UI state during the evening review. */
export interface TaskReviewState {
  done: boolean;
  defer: boolean;
  deferDate: string; // ISO date string for the target defer date
}

interface Props {
  task: Task;
  state: TaskReviewState;
  today: string;
  onDone: (v: boolean) => void;
  onDefer: (v: boolean) => void;
  onDeferDate: (v: string) => void;
}

export function EveningTaskReviewRow({ task, state, today, onDone, onDefer, onDeferDate }: Props) {
  const isOverdue = task.dueDate < today;

  return (
    <div className={`rounded-2xl border px-3 py-2.5 ${state.done ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10" : "border-ink-200 bg-ink-50"}`}>
      <div className="flex items-center gap-3">
        {/* Done checkbox */}
        <button
          onClick={() => onDone(!state.done)}
          className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 transition ${
            state.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-ink-300 hover:border-emerald-400"
          }`}
          aria-label="Hoàn thành"
        >
          {state.done && <span className="text-[10px] font-bold">✓</span>}
        </button>

        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: PRIO[task.priority] }} />

        <div className="min-w-0 flex-1">
          <p className={`truncate text-[13px] font-semibold ${state.done ? "line-through text-ink-400" : ""}`}>
            {task.title}
          </p>
          <p className={`text-[11px] ${isOverdue && !state.done ? "text-rose-500 font-semibold" : "text-ink-400"}`}>
            {task.dueDate}{isOverdue && !state.done ? " · Quá hạn" : ""}
          </p>
        </div>

        {/* Defer toggle — only visible when task is not done */}
        {!state.done && (
          <button
            onClick={() => onDefer(!state.defer)}
            className={`shrink-0 rounded-lg px-2 py-1 text-[11px] font-bold transition ${
              state.defer ? "bg-amber-400 text-white" : "bg-ink-100 text-ink-500 hover:bg-amber-100"
            }`}
          >
            Dời
          </button>
        )}
      </div>

      {/* Defer date picker — shown when defer is toggled */}
      {state.defer && !state.done && (
        <div className="mt-2 flex items-center gap-2 pl-8">
          <span className="text-[12px] text-ink-500">Dời sang:</span>
          <BentoDateInput
            value={state.deferDate}
            min={addDaysIso(1)}
            onChange={(e) => onDeferDate(e.target.value)}
            className="h-9 rounded-lg px-2 py-1 text-[12px] focus:border-brand-400"
          />
        </div>
      )}
    </div>
  );
}
